@if (header() === 'dialog') {
    <qpx-dialog-header
        class="dialog-header flex-none"
        title="Quippy"
        [titleId]="titleId"
        closeLabel="Close Ask Quippy"
        (dismiss)="closeChat.emit()"
    >
        <ng-container actions [ngTemplateOutlet]="headerActions" />
    </qpx-dialog-header>
} @else {
    <qpx-header
        class="header qpx-content-width w-full flex-none flex-wrap gap-4"
        heading="Quippy"
        [titleId]="titleId"
    >
        <p subtitle qpxBody="secondary" tone="muted">
            {{ assistantCopy().subtitle }}
        </p>

        <ng-container actions>
            <ng-container [ngTemplateOutlet]="headerActions" />
        </ng-container>
    </qpx-header>
}

<ng-template #headerActions>
    <div class="inline-flex flex-wrap items-center gap-2">
        @if (canUseAdminMode()) {
            <mat-checkbox
                qpx
                [id]="adminModeId"
                context="primary"
                aria-label="Admin mode"
                [checked]="isAdminModeEnabled()"
                (change)="onAdminModeChanged($event.checked)"
                class="admin-mode-toggle"
            >
                Admin mode
            </mat-checkbox>
        }

        <button
            qpxButton
            [size]="header() === 'page' && isPhone() ? 'sm' : 'md'"
            matButton="filled"
            context="primary"
            class="min-w-fit"
            (click)="newChat.emit()"
        >
            <ng-icon qpx matButtonIcon size="sm" name="lucidePencil" />
            <span>New chat</span>
        </button>

        @if (header() === 'page') {
            <button
                #chatHistoryMenuTrigger="matMenuTrigger"
                qpxButton
                [size]="isPhone() ? 'sm' : 'md'"
                [matButton]="
                    appearance() === 'outlined' ? 'outlined' : 'elevated'
                "
                [matMenuTriggerFor]="chatHistoryMenu"
                aria-label="Chat history"
                class="icon-only"
                (menuOpened)="onChatHistoryOpened()"
            >
                <ng-icon qpx matButtonIcon size="sm" name="lucideHistory" />
                <span class="sr-only">Chat history</span>
            </button>
            <mat-menu
                #chatHistoryMenu="matMenu"
                class="w-md h-md-max"
                aria-label="Chat history"
            >
                <ng-template matMenuContent>
                    <div
                        #chatHistory
                        qpx-quippy-chat-history
                        [currentChatRef]="chatRef()"
                        [chats]="sidebarThreads()"
                        (selectChat)="onSwitchThread($event)"
                    ></div>
                </ng-template>
            </mat-menu>
        }
    </div>
</ng-template>

<qpx-quippy-thread
    [appearance]="appearance()"
    [chatId]="currentChatId()"
    [class.category-first]="showSuggestions()"
    [messages]="messages()"
    [isThreadLoading]="isLoading()"
    [sourceFallbackBuildingId]="sourceFallbackBuildingId()"
    [allowPackDiscovery]="outcomeSelection.packAvailable()"
    (preparePack)="onQuestionSubmitted($event)"
    (retry)="onRetry($event)"
    (editResend)="onEditResend($event)"
    (documentOptionSelected)="onDocumentOptionSelected($event)"
    class="min-h-0 flex-auto"
/>

@if (showSuggestions()) {
    <div
        #suggestionsPane
        class="suggestions-pane category-first qpx-content-width"
        [class.dialog-suggestions]="header() === 'dialog'"
    >
        <qpx-quippy-outcome-cards
            [scopeKind]="quippyScope().kind"
            [packAvailable]="outcomeSelection.packAvailable()"
            [selected]="outcomeSelection.selected()"
            [appearance]="appearance()"
            [disabled]="isLoading()"
            [headingId]="titleId"
            (outcomeSelected)="outcomeSelection.activate($event)"
        />
        <qpx-quippy-suggested-questions
            class="suggestions"
            heading="Try asking:"
            [headingId]="`${titleId}-suggestions`"
            [categoryFirst]="outcomeSelection.showCategories()"
            [initialCategory]="outcomeSelection.initialCategory()"
            [showCategories]="outcomeSelection.showCategories()"
            [resetKey]="`${currentChatId() ?? 'new'}:${isAdminModeEnabled()}:${outcomeSelection.selected()}:${outcomeSelection.activation()}`"
            ariaLabel="Suggested prompts"
            [appearance]="appearance()"
            [questions]="outcomeSelection.questions()"
            [pageSize]="suggestionPageSize()"
            [showLeadingAvatar]="false"
            (questionSelected)="onPromptSelected($event)"
        />
    </div>
}

<div #composer class="composer panel-main-input qpx-content-width flex-none">
    <ng-template #composerScope>
        @if (hasSentMessage()) {
            <div class="scope-actions">
                @if (header() === 'dialog') {
                    <button
                        qpxButton
                        matButton="outlined"
                        size="sm"
                        (click)="onOpenInPage()"
                    >
                        <ng-icon qpx matButtonIcon name="lucideExternalLink" />
                        <span>Open in page</span>
                    </button>
                } @else {
                    <button
                        qpxButton
                        matButton="outlined"
                        size="sm"
                        (click)="onOpenInDialog()"
                    >
                        <ng-icon qpx matButtonIcon name="lucideExternalLink" />
                        <span>Open in dialog</span>
                    </button>
                }
                <button
                    qpxButton
                    matButton="outlined"
                    size="sm"
                    class="icon-only scope-dismiss"
                    aria-label="Hide chat actions"
                    (click)="scopeRowDismissed.set(true)"
                >
                    <ng-icon qpx matButtonIcon size="sm" name="lucideX" />
                </button>
            </div>
        } @else if (scopeSelector()?.templateRef; as selector) {
            <ng-container [ngTemplateOutlet]="selector" />
        }
    </ng-template>
    <qpx-quippy-question-box
        #questionBox
        formAriaLabel="Send a message to Quippy"
        sendAriaLabel="Send message"
        [ariaDescribedBy]="inputHintId"
        [appearance]="appearance()"
        [compact]="header() === 'dialog'"
        [maxRows]="(chatHeight() ?? 1000) < 500 ? 3 : 10"
        [placeholder]="assistantCopy().inputPlaceholder"
        [question]="draft()"
        [scopeRowPosition]="scopeRowPosition()"
        [scopeRowDismissed]="scopeRowDismissed()"
        [scopeTemplate]="composerScope"
        [sendDisabled]="isLoading()"
        (questionChange)="onInput($event)"
        (questionSubmitted)="onQuestionSubmitted($event)"
        class="question-box"
    />

    <p [id]="inputHintId" class="input-hint">
        Quippy can make mistakes, so verify important details
        <span aria-hidden="true"> • </span>
        Quippy cannot query private documents
    </p>
</div>
